import { useEffect, useState } from 'react';
import { LogIn, User, AlertTriangle, CheckCircle, Shield } from 'lucide-react';
import * as authApi from '../utils/authApi';
import Button from './elements/Button';
import Input from './elements/Input';
import { validatePasswordStrength, getPasswordStrength } from '../utils/passwordValidator';
import { fetchLicense } from '../utils/subscriptionApi';
import { useSearchParams } from 'react-router-dom';
import Info from './elements/Info';
import ConsentModal from './ConsentModal';
import QuickUnlockSetupPrompt from './QuickUnlockSetupPrompt';
import { shouldOfferQuickUnlockSetup } from '../utils/quickUnlock';
import { getDateDiff, isValidDate, toSeconds } from '../utils/globalUtils';
import AcknowledgementChecklist from './AcknowledgementChecklist';
import {
  acknowledgementRefusal, allAcknowledged, emptyAcknowledgements,
} from '../utils/signupAcknowledgements';

const LAST_ENTERED_EMAIL_KEY = 'lastEnteredEmail';

const readLastEnteredEmail = () => {
  try {
    return localStorage.getItem(LAST_ENTERED_EMAIL_KEY) || '';
  } catch {
    return '';
  }
};

const rememberLastEnteredEmail = (value) => {
  try {
    localStorage.setItem(LAST_ENTERED_EMAIL_KEY, value);
  } catch {
    // Ignore storage failures (private browsing/quota).
  }
};

const forgetLastEnteredEmail = () => {
  try {
    localStorage.removeItem(LAST_ENTERED_EMAIL_KEY);
  } catch {
    // Ignore storage failures
  }
};

const AuthForm = ({ onAuthSuccess, onAuthModeChange }) => {
  const isSignupEnabled = process.env.REACT_APP_SIGNUP_ENABLED === 'true';
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(true);
  const [showResetRequest, setShowResetRequest] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [email, setEmail] = useState(() => readLastEnteredEmail());
  const [rememberMe, setRememberMe] = useState(() => !!readLastEnteredEmail());
  const [password, setPassword] = useState('');
  const [dob, setDob] = useState('');
  const [referralCode, setReferralCode] = useState(searchParams.get('ref') || '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showLoginVerify, setShowLoginVerify] = useState(false);
  const [verifyToken, setVerifyToken] = useState('');
  const [passwordStrength, setPasswordStrength] = useState('weak');
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [acknowledgements, setAcknowledgements] = useState(emptyAcknowledgements);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [pendingLoginData, setPendingLoginData] = useState(null);
  const [isSubmittingConsent, setIsSubmittingConsent] = useState(false);
  const [showQuickUnlockPrompt, setShowQuickUnlockPrompt] = useState(false);

  useEffect(() => {
    setError(null);
  }, [successMessage]);

  useEffect(() => {
    setSuccessMessage(null);
  }, [error]);

  useEffect(() => {
    if (referralCode && isSignupEnabled) {
      setIsLogin(false);
    }
  }, [referralCode, isSignupEnabled]);

  useEffect(() => {
    if (onAuthModeChange) {
      onAuthModeChange(isLogin);
    }
  }, [isLogin, onAuthModeChange]);

  const handleEmailChange = (value) => {
    setEmail(value);
    // Only remember if rememberMe is checked
    if (rememberMe) {
      rememberLastEnteredEmail(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    // Validate password strength for signup and reset
    if (!isLogin || showResetForm) {
      const validation = validatePasswordStrength(showResetForm ? resetNewPassword : password);
      if (!validation.isValid) {
        setError(`Password requirements: ${validation.errors.join(', ')}`);
        return;
      }
    }

    // Handle Remember Me logic
    if (isLogin) {
      if (rememberMe) {
        rememberLastEnteredEmail(email);
      } else {
        forgetLastEnteredEmail();
      }
    }

    setIsLoading(true);

    try {
      if (isLogin) {
        const result = await authApi.login(email, password);

        if (result.success) {
          setVerifyToken('');
          setSuccessMessage(result.message);
          setShowLoginVerify(true);
        } else {
          setError(result.message);
        }
      } else {
        if (!isSignupEnabled) {
          setError('Sign up is temporarily disabled. Please try again later.');
          return;
        }
        // Named, not bundled: the whole point of C3 is that a user can accept two of these
        // and decline the third, so the refusal has to say which one is outstanding rather
        // than reprint the combined sentence the split retired. It names them rather than
        // QUOTING them - the labels are first-person tickbox wording, and joining three of
        // them into one line made the app answer a refusal with "I consent to Eternal
        // Locker processing my personal data... I accept the Terms & Conditions..." (EL-33).
        if (!allAcknowledged(acknowledgements)) {
          setError(acknowledgementRefusal(acknowledgements));
          return;
        }
        
        const age = getDateDiff(new Date(dob));
        if (age && age.years < 18) {
          setError('You must be at least 18 years old to sign up.');
          return;
        }

        const result = await authApi.signup(email, password, referralCode, acknowledgements, toSeconds(new Date(dob)));
        if (result.success) {
          setSuccessMessage(result.message || 'Account created successfully!');
          setIsLogin(true);
        } else {
          setError(result.message);
        }
      }
    } catch (e) {
      setError('A network error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const completeLogin = (result) => {
    sessionStorage.setItem('isLoggedIn', 'true');
    sessionStorage.setItem('email', result.email);
    sessionStorage.setItem('role', result.role);
    sessionStorage.setItem('theme', result.theme);
    document.documentElement.setAttribute('data-theme', result.theme);
    document.documentElement.style.colorScheme = result.theme;
    if (result.firstName) sessionStorage.setItem('firstName', result.firstName);
    if (result.lastName) sessionStorage.setItem('lastName', result.lastName);

    fetchLicense().then((res) => {
      if (res.success && res.license) {
        sessionStorage.setItem('license', JSON.stringify(res.license));
      }
    }).catch(() => {
      // Ignore license fetch errors
    });

    if (shouldOfferQuickUnlockSetup()) {
      setShowQuickUnlockPrompt(true);
    } else {
      onAuthSuccess();
    }
  };

  const handleQuickUnlockPromptDone = () => {
    setShowQuickUnlockPrompt(false);
    onAuthSuccess();
  };

  // The modal hands back WHICH acknowledgements were ticked rather than a bare "yes", so
  // the re-consent path records the same three facts signup does (C3).
  const handleConsentSubmit = async (reconsentAcknowledgements) => {
    setIsSubmittingConsent(true);
    setError(null);

    try {
      // Call update-consent endpoint
      const result = await authApi.updateConsent(reconsentAcknowledgements);

      if (result.success) {
        // Complete the login process
        completeLogin(pendingLoginData);
        setShowConsentModal(false);
        setPendingLoginData(null);
      } else {
        setError(result.message || 'Failed to update consent. Please try again.');
      }
    } catch (e) {
      setError('A network error occurred while updating consent. Please try again.');
    } finally {
      setIsSubmittingConsent(false);
    }
  };

  const handleConsentReject = () => {
    setShowConsentModal(false);
    setPendingLoginData(null);
    setShowLoginVerify(false);
    setVerifyToken('');
    setError('You have declined the updated terms. Please accept to continue using Eternal Locker.');
  };

  const handleLoginVerify = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setIsLoading(true);

    try {
      const result = await authApi.login(email, password, verifyToken);

      if (result.success) {
        // Check if user needs to re-consent to updated policy
        if (result.requiresConsentUpdate) {
          setPendingLoginData(result);
          setShowConsentModal(true);
          setShowLoginVerify(false);
          setIsLoading(false);
          return;
        }

        completeLogin(result);
      } else {
        setError(result.message);
      }
    } catch (e) {
      setError('A network error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <ConsentModal
        isOpen={showConsentModal}
        onConsent={handleConsentSubmit}
        onReject={handleConsentReject}
        isLoading={isSubmittingConsent}
      />

      <QuickUnlockSetupPrompt
        open={showQuickUnlockPrompt}
        accountEmail={email}
        onDone={handleQuickUnlockPromptDone}
      />

      <div className="w-full max-w-md px-8 pt-8 space-y-6 bg-card-background rounded-xl font-sans">
        <h2 className="text-2xl font-bold text-primary-dark text-center flex items-center justify-center">
          {(showResetRequest || showResetForm || showLoginVerify || isLogin) ? (
            <LogIn className="mr-3 text-accent-main" />
          ) : (
            <User className="mr-3 text-accent-main" />
          )}
          {showLoginVerify
            ? 'Verify Login'
            : showResetRequest
              ? 'Reset Password'
              : showResetForm
                ? 'Set New Password'
                : isLogin
                  ? 'Log In'
                  : 'Create Account'}
        </h2>
        <p className="text-sm text-center text-text-muted">
          Your digital assets, secured forever.
        </p>
        {!isSignupEnabled && (
          <Info type="warning" className="mb-6">
            New user sign up is temporarily unavailable.
          </Info>
        )}

        {error && (
          <Info type="error" className="mb-6">
            {error}
          </Info>
        )}

        {successMessage && (
          <Info type="success" className="mb-6">
            {successMessage}
          </Info>
        )}

        {!showLoginVerify && !showResetRequest && !showResetForm && (
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            id="email"
            label="Email Address"
            type="email"
            value={email}
            onChange={(e) => handleEmailChange(e.target.value)}
            required
            placeholder="you@example.com"
            disabled={isLoading}
            autoFocus
          />
          {isLogin && (
            <label htmlFor="rememberMe" className="flex items-start gap-2 text-xs text-text-muted">
              <input
                id="rememberMe"
                type="checkbox"
                checked={rememberMe}
                onChange={e => {
                  setRememberMe(e.target.checked);
                  if (!e.target.checked) {
                    forgetLastEnteredEmail();
                  } else {
                    rememberLastEnteredEmail(email);
                  }
                }}
                disabled={isLoading}
                className="mt-0.5"
              />
              <span>Remember Me</span>
            </label>
          )}
          <div>
            {!isLogin && (
              <Input
                id="dob"
                label="Date of Birth"
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                required
                disabled={isLoading}
                error={dob && getDateDiff(new Date(dob)).years < 18 ? 'You must be at least 18 years old' : null}
              />
            )}

            <Input
              id="password"
              label="Password"
              type="password"
              value={password}
              onChange={(e) => {
                const newPassword = e.target.value;
                setPassword(newPassword);
                if (!isLogin) {
                  const validation = validatePasswordStrength(newPassword);
                  setPasswordErrors(validation.errors);
                  setPasswordStrength(getPasswordStrength(newPassword));
                }
              }}
              required
              placeholder="••••••••"
              disabled={isLoading}
            />

            {!isLogin && (
              <Input
                id="referralCode"
                label={`Referral Code${!referralCode ? ' (optional)' : ''}`}
                type="text"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value)}
                placeholder="Enter referral code"
                visible={referralCode !== ''} // Show if there's a code or if it came from URL
                disabled={true} // Disable editing referral code
              />
            )}
            {!isLogin && password && (
              <div className="mt-2 space-y-1">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  <span className={`text-xs font-medium ${
                    passwordStrength === 'strong' ? 'text-green-600' :
                    passwordStrength === 'medium' ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    Strength: {passwordStrength.toUpperCase()}
                  </span>
                </div>
                {passwordErrors.length > 0 && (
                  <ul className="text-xs text-red-600 ml-6 list-disc">
                    {passwordErrors.map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          {!isLogin && (
            <AcknowledgementChecklist
              acknowledgements={acknowledgements}
              onChange={setAcknowledgements}
              disabled={isLoading}
              idPrefix="signup"
            />
          )}
          <Button
            type="submit"
            className="w-full"
            disabled={isLoading}
            loading={isLoading}
          >
            {isLogin ? (
              <span className="flex items-center"><LogIn className="w-5 h-5 mr-2" /> Log In</span>
            ) : (
              <span className="flex items-center"><User className="w-5 h-5 mr-2" /> Sign Up</span>
            )}
          </Button>
        </form>
      )}

      {showResetRequest && (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setIsLoading(true);
            setError(null);
            setSuccessMessage(null);
            try {
              const result = await authApi.requestPasswordReset(email);
              if (result.success) {
                setSuccessMessage(result.message);
              } else {
                setError(result.message || 'Failed to request password reset.');
              }
              setShowResetRequest(false);
              setShowResetForm(true);
            } catch (e) {
              setError('Failed to request password reset.');
            } finally {
              setIsLoading(false);
            }
          }}
          className="space-y-4"
        >
          <Input
            id="resetEmail"
            label="Enter your email to reset password"
            type="email"
            value={email}
            onChange={(e) => handleEmailChange(e.target.value)}
            required
            placeholder="you@example.com"
            disabled={isLoading}
          />
          <Button
            type="submit"
            className="w-full"
            disabled={isLoading}
          >
            Request Reset Token
          </Button>
        </form>
      )}

      {showResetForm && (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setIsLoading(true);
            setError(null);
            setSuccessMessage(null);
            try {
              const result = await authApi.resetPassword(email, resetToken, resetNewPassword);
              if (result.success) {
                setTimeout(() => {
                  setShowResetForm(false);
                  setIsLogin(true);
                  setSuccessMessage('Your password has been reset. Please log in with your new password.');
                  setResetToken('');
                  setResetNewPassword('');
                }, 1200);
              } else {
                setError(result.message || 'Password reset failed.');
              }
            } catch (e) {
              setError('Password reset failed.');
            } finally {
              setIsLoading(false);
            }
          }}
          className="space-y-4"
        >
          <Input
            id="resetToken"
            label="Enter Reset Token"
            value={resetToken}
            onChange={(e) => setResetToken(e.target.value)}
            required
            placeholder="Paste your reset token here"
            disabled={isLoading}
            autoFocus
          />
          <div>
            <Input
              id="resetNewPassword"
              label="New Password"
              type="password"
              value={resetNewPassword}
              onChange={(e) => {
                const newPassword = e.target.value;
                setResetNewPassword(newPassword);
                const validation = validatePasswordStrength(newPassword);
                setPasswordErrors(validation.errors);
                setPasswordStrength(getPasswordStrength(newPassword));
              }}
              required
              placeholder="Enter new password"
              disabled={isLoading}
            />
            {resetNewPassword && (
              <div className="mt-2 space-y-1">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  <span className={`text-xs font-medium ${
                    passwordStrength === 'strong' ? 'text-green-600' :
                    passwordStrength === 'medium' ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    Strength: {passwordStrength.toUpperCase()}
                  </span>
                </div>
                {passwordErrors.length > 0 && (
                  <ul className="text-xs text-red-600 ml-6 list-disc">
                    {passwordErrors.map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          <Button
            type="submit"
            className="w-full"
            disabled={isLoading}
          >
            Reset Password
          </Button>
        </form>
      )}

      {showLoginVerify && (
        <div>
          <form
            onSubmit={handleLoginVerify}
            className="space-y-4"
          >
            <Input
              id="verifyToken"
              label="Enter Verification Code"
              value={verifyToken}
              onChange={(e) => setVerifyToken(e.target.value)}
              required
              placeholder="Enter code from email"
              disabled={isLoading}
              autoFocus
            />
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
            >
              <LogIn className="w-5 h-5 mr-2" /> Verify and Login
            </Button>
          </form>
          <div className="text-center text-sm text-text-muted mt-4">
            Didn't receive a code? Check your spam folder or
            <Button type='anchor' onClick={handleSubmit} disabled={isLoading} className="ml-1">
              Resend the email
            </Button>
          </div>
          <div className="flex text-center justify-center mt-4">
            <Button
              type='anchor'
              disabled={isLoading}
              onClick={() => {
                setShowLoginVerify(false);
                setVerifyToken('');
                setSuccessMessage(null);
                setError(null);
              }}
            >
              Back to login
            </Button>
          </div>
        </div>
      )}

      <div className="text-center text-sm flex flex-col ">
        {!showLoginVerify && !showConsentModal && (
          isSignupEnabled && (
            <Button
              type='anchor'
              onClick={() => {
                // EL-33. THIS USED TO THROW, AND THE TWO LINES BELOW IT NEVER RAN. The C3
                // split replaced the single `consentAccepted` boolean with the three-key
                // `acknowledgements` object, and this handler kept calling the setter that
                // went with it - a ReferenceError every time anyone switched between Log In
                // and Sign Up. Nothing showed it: React does not catch errors thrown in an
                // event handler, so the visible symptom was the *later* statements silently
                // not happening, and "Invalid email or password." from a failed login stayed
                // on screen under the Create Account heading, telling someone their password
                // was wrong for an account they had not made yet.
                //
                // The clear stays: switching forms unticks the boxes, which is what the dead
                // line was for. A consent is given on the form it is given on, and carrying
                // three ticks across a form switch would make it look otherwise.
                setIsLogin(!isLogin);
                setShowLoginVerify(false);
                setShowResetRequest(false);
                setShowResetForm(false);
                setAcknowledgements(emptyAcknowledgements());
                setSuccessMessage(null);
                setError(null);
              }}
              disabled={isLoading}
            >
              {isLogin ? "Need an account? Sign Up" : "Already have an account? Log In"}
            </Button>
          )
        )}
        {isLogin && (showResetRequest || showResetForm) && (
          <Button
            type='anchor'
            onClick={() => {
              setShowLoginVerify(false);
              setShowResetRequest(false);
              setShowResetForm(false);
              setResetToken('');
              setResetNewPassword('');
              setSuccessMessage(null);
              setError(null);
            }}
            disabled={isLoading}
          >
            Back to login
          </Button>
        )}
        {isLogin && !showLoginVerify && !showResetRequest && !showResetForm && (
          <Button
            type='anchor'
            onClick={() => {
              setShowResetRequest(true);
              setShowLoginVerify(false);
              setShowResetForm(false);
              setSuccessMessage(null);
              setError(null);
            }}
            disabled={isLoading}
          >
            Forgot password?
          </Button>
        )}
      </div>
    </div>
    </>
  );
};

export default AuthForm;